import { useEffect, useState } from 'react';
import Icon from '../components/icon';
import TextField from '../components/text-field';
import {
  changePassword,
  listProfileSessions,
  requestEmailChange,
  revokeAllProfileSessions,
  revokeProfileSession,
  updateProfile,
} from '../api/panel-api';
import {
  emailErrorText,
  emailPattern,
  formatDateTime,
  formatPhone,
  isCompletePhone,
  panelRoleLabel,
  passwordRules,
  phoneErrorText,
} from '../data/panel';

function sessionTitle(session) {
  const parts = [];
  if (session.is_current) parts.push('Этот компьютер');
  if (session.os) parts.push(session.os);
  if (session.browser) parts.push(session.browser);
  return parts.join(', ') || 'Сессия';
}

function ProfilePage({ profile, lastLogin, onSignOut, onSaved }) {
  const [editing, setEditing] = useState(Boolean(profile.must_change_password));
  const [form, setForm] = useState(() => fieldsFrom(profile));
  const [sessions, setSessions] = useState([]);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [notice, setNotice] = useState('');
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listProfileSessions()
      .then((data) => {
        if (!cancelled) setSessions(data.items || []);
      })
      .catch((loadError) => {
        if (!cancelled) setError(loadError.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function setField(key, value) {
    const nextValue = key === 'phone' ? formatPhone(value) : value;
    setForm((current) => ({ ...current, [key]: nextValue }));
    setFieldErrors((current) => {
      const next = { ...current, [key]: '' };
      if (key === 'nextPassword' || key === 'repeatPassword') {
        next.repeatPassword = '';
      }
      return next;
    });
  }

  function passwordValid() {
    return Boolean(form.currentPassword)
      && form.nextPassword.length > 0
      && form.nextPassword === form.repeatPassword
      && passwordRules.every((rule) => rule.test(form.nextPassword));
  }

  const repeatMismatch = Boolean(form.repeatPassword)
    && form.nextPassword !== form.repeatPassword;
  const repeatError = fieldErrors.repeatPassword
    || (repeatMismatch ? 'Пароли не совпадают' : '');
  const canChangePassword = passwordValid();
  const requiredFilled = Boolean(
    form.firstName && form.lastName && form.email && form.phone && form.position,
  );

  function cancelEdit() {
    setForm(fieldsFrom(profile));
    setEditing(false);
    setFieldErrors({});
    setError('');
    setNotice('');
  }

  function passwordFilled() {
    return Boolean(form.currentPassword || form.nextPassword || form.repeatPassword);
  }

  async function submitPassword() {
    await changePassword({
      current_password: form.currentPassword,
      new_password: form.nextPassword,
      new_password_repeat: form.repeatPassword,
    });
    await onSignOut();
  }

  async function save() {
    if (!requiredFilled) return;
    const nextFieldErrors = {};
    if (!emailPattern.test(form.email)) nextFieldErrors.email = emailErrorText;
    if (!isCompletePhone(form.phone)) nextFieldErrors.phone = phoneErrorText;
    if (Object.keys(nextFieldErrors).length) {
      setFieldErrors(nextFieldErrors);
      return;
    }
    setPending(true);
    setError('');
    setNotice('');
    setFieldErrors({});
    try {
      if (passwordFilled() && !passwordValid()) {
        setFieldErrors({
          repeatPassword: repeatMismatch ? 'Пароли не совпадают' : '',
          nextPassword: passwordRules.every((rule) => rule.test(form.nextPassword))
            ? ''
            : 'Пароль не соответствует требованиям',
          currentPassword: form.currentPassword ? '' : 'Введите текущий пароль',
        });
        return;
      }
      const patch = {};
      if (form.firstName !== profile.first_name) patch.first_name = form.firstName;
      if (form.lastName !== profile.last_name) patch.last_name = form.lastName;
      if (form.phone !== formatPhone(profile.phone)) patch.phone = form.phone;
      if (form.position !== profile.job_title) patch.job_title = form.position;
      let nextProfile = profile;
      if (Object.keys(patch).length) {
        nextProfile = await updateProfile(patch);
      }
      if (form.email !== profile.email) {
        const emailResult = await requestEmailChange(form.email);
        setNotice(emailResult.mail_sent === false
          ? 'До перехода по ссылке логином остаётся прежняя почта.'
          : 'Письмо уходит на новый адрес. До перехода по ссылке логином остаётся прежняя почта.');
        setForm((current) => ({ ...current, email: profile.email }));
      }
      if (passwordFilled()) {
        await submitPassword();
        return;
      }
      onSaved(nextProfile);
      setEditing(false);
    } catch (saveError) {
      setError(saveError.message);
      const fields = saveError.fields || {};
      setFieldErrors({
        firstName: fields.first_name,
        lastName: fields.last_name,
        phone: fields.phone,
        position: fields.job_title,
        email: fields.email,
        currentPassword: fields.current_password,
        nextPassword: fields.new_password,
        repeatPassword: fields.new_password_repeat,
      });
    } finally {
      setPending(false);
    }
  }

  async function changeOwnPassword() {
    if (!canChangePassword) return;
    setPending(true);
    setError('');
    setFieldErrors({});
    try {
      await submitPassword();
    } catch (saveError) {
      setError(saveError.message);
      const fields = saveError.fields || {};
      setFieldErrors({
        currentPassword: fields.current_password,
        nextPassword: fields.new_password,
        repeatPassword: fields.new_password_repeat,
      });
      setPending(false);
    }
  }

  async function endSession(id) {
    setError('');
    try {
      await revokeProfileSession(id);
      setSessions((list) => list.filter((item) => item.id !== id));
    } catch (sessionError) {
      setError(sessionError.message);
    }
  }

  async function endAll() {
    setError('');
    try {
      await revokeAllProfileSessions();
    } catch {
      // Даже если отзыв не подтверждён, локальный выход всё равно нужен.
    }
    await onSignOut();
  }

  return (
    <section className="content content--plain" aria-label="Мой профиль">
      <article className="card">
        <div>
          <div className="card__head profile-head">
            <h1 className="card-title">Мой профиль</h1>
            {editing ? null : (
              <button type="button" className="btn btn--secondary" onClick={() => setEditing(true)}>
                <Icon name="pencil" size={16} />
                Редактировать профиль
              </button>
            )}
          </div>
          <div className="meta-row" style={{ marginTop: 20 }}>
            <div className="meta"><span>Роль в панели</span><strong>{panelRoleLabel[profile.role] || profile.role}</strong></div>
            <div className="meta"><span>Последний вход</span><strong>{lastLogin}</strong></div>
          </div>
        </div>
        <div>
          {error ? <p className="empty">{error}</p> : null}
          {notice ? <p className="empty">{notice}</p> : null}
          <div className="profile-form">
            <div className="profile-fields">
              <div className="field-row">
                <TextField label="Имя" value={form.firstName} readOnly={!editing} error={fieldErrors.firstName} onChange={(value) => setField('firstName', value)} />
                <TextField label="Фамилия" value={form.lastName} readOnly={!editing} error={fieldErrors.lastName} onChange={(value) => setField('lastName', value)} />
              </div>
              <div className="field-row">
                <TextField label="Электронная почта" value={form.email} readOnly={!editing} error={fieldErrors.email} onChange={(value) => setField('email', value)} />
                <TextField label="Телефон" value={form.phone} readOnly={!editing} error={fieldErrors.phone} onChange={(value) => setField('phone', value)} />
              </div>
              <div className="field-row">
                <div className="profile-fields__half">
                  <TextField label="Должность" value={form.position} readOnly={!editing} error={fieldErrors.position} onChange={(value) => setField('position', value)} />
                </div>
              </div>
            </div>
            <div className="profile-password">
              <h2 className="profile-password__title">Пароль</h2>
              {editing ? (
                <div className="profile-password__body">
                  <TextField label="Текущий пароль" type="password" trailing="password" required={false} autoComplete="current-password" value={form.currentPassword} error={fieldErrors.currentPassword} onChange={(value) => setField('currentPassword', value)} />
                  <div className="profile-password__new">
                    <TextField label="Новый пароль" type="password" trailing="password" required={false} autoComplete="new-password" value={form.nextPassword} error={fieldErrors.nextPassword} onChange={(value) => setField('nextPassword', value)} />
                    <TextField label="Повторите пароль" type="password" trailing="password" required={false} autoComplete="new-password" value={form.repeatPassword} error={repeatError} onChange={(value) => setField('repeatPassword', value)} />
                    <ul className="rules">
                      {passwordRules.map((rule) => (
                        <li key={rule.id} className={rule.test(form.nextPassword) ? 'is-met' : ''}>
                          <Icon name="check" size={12} tone="inherit" />
                          {rule.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button type="button" className="btn btn--secondary" disabled={pending || !canChangePassword} onClick={changeOwnPassword}>Сменить пароль</button>
                </div>
              ) : (
                <div className="profile-password__current">
                  <TextField label="Текущий пароль" value="••••••••" readOnly onChange={() => {}} />
                </div>
              )}
            </div>
          </div>
          {editing ? (
            <div className="form-actions profile-actions">
              <button type="button" className="btn btn--secondary" onClick={cancelEdit}>Отмена</button>
              <button type="button" className="btn" disabled={pending || !requiredFilled} onClick={save}>Сохранить</button>
            </div>
          ) : null}
        </div>
      </article>
      <article className="card" style={{ gap: 16 }}>
        <h2 className="section-access__title">Активные сессии</h2>
        <div>
          {sessions.map((session) => {
            const stamp = formatDateTime(session.last_used_at);
            return (
              <div className="session" key={session.id}>
                <div style={{ flex: 1 }}>
                  <p>{sessionTitle(session)}</p>
                  <small>{stamp.text}</small>
                </div>
                {session.is_current ? null : (
                  <button type="button" className="btn btn--ghost btn--small" onClick={() => endSession(session.id)}>
                    Завершить
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <button type="button" className="btn btn--danger-text sessions-end" onClick={endAll}>
          <Icon name="sign-out" size={16} tone="inherit" />
          Завершить все сессии
        </button>
      </article>
    </section>
  );
}

function fieldsFrom(profile) {
  return {
    firstName: profile.first_name,
    lastName: profile.last_name,
    email: profile.email,
    phone: formatPhone(profile.phone),
    position: profile.job_title,
    currentPassword: '',
    nextPassword: '',
    repeatPassword: '',
  };
}

export default ProfilePage;
